import useTranslator from "../../hooks/useTranslator";
import CheckIcon0 from "../../assets/img/checkIcon02.svg";
import Twotone from "../../assets/img/Twotone.svg";
import {
  checkSalary,
  convertCurrencyFormat,
  haveValue,
  formatToStandardDate,
  getUpdatedExchangeIncome,
} from "../../helper/common";
import { useContext, useEffect, useState } from "react";
import moment from "moment";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import { calculateModelo130Income } from "../../helper/selfEmployedIncomeCalculation";

const DetailsIncomeInfo = (props) => {
  const {
    user = {},
    forensicAnalysis = null,
    handleInfoModal,
    isFromReport,
    llShowOnly,
  } = props;
  const { _t } = useTranslator();
  const { user: originalUser } = useContext(AuthContext);
  const { deaWorkspace } = useContext(LandLordContext);

  const [recordedSalary, setRecordedSalary] = useState({
    salary: 0,
    matched: 0,
  });

  const declaredIncome = getUpdatedExchangeIncome(
    user?.current_annual_income,
    user?.currency,
    originalUser
  );

  useEffect(() => {
    if (haveValue(user)) {
      const temp_sal = checkSalary(user);
      setRecordedSalary(temp_sal);
    }
  }, [user]);

  const taxReturns =
    (forensicAnalysis?.["Tax Returns"] ?? forensicAnalysis?.["Tax returns"])
      ?.dataInJSON?.taxReturns ?? [];

  const getQuarterNumber = (taxReturn = {}) => {
    // #NOTE: new field that will be added to the taxReturns in the BE
    const quarter = Number(taxReturn?.quarterNumber);
    return Number.isFinite(quarter) && quarter >= 1 && quarter <= 4
      ? quarter
      : null;
  };

  const latestYear = taxReturns.reduce((maxYear, tr) => {
    const year = Number(tr?.taxReturnYear);
    return Number.isFinite(year) ? Math.max(maxYear, year) : maxYear;
  }, 0);

  const getFallbackAnnualIncome = (taxReturn = {}) =>
    taxReturn?.taxReportOrigin?.toLowerCase() !== "local"
      ? taxReturn?.grossIncome
      : (taxReturn?.combinedIncome ?? taxReturn?.grossIncome);

  const hasValidFallbackIncome = (taxReturn = {}) => {
    const value = getFallbackAnnualIncome(taxReturn);
    return (
      value !== null && value !== undefined && Number.isFinite(Number(value))
    );
  };

  // Check if an annual report exists for the latest year — annual takes priority over quarterly
  const latestYearAnnualReturn = taxReturns
    .filter(
      (tr) =>
        tr?.reportType === "annual" && Number(tr?.taxReturnYear) === latestYear
    )
    .find((tr) => hasValidFallbackIncome(tr));

  const latestYearTaxReturns = taxReturns
    .filter(
      (tr) => Number(tr?.taxReturnYear) === latestYear && tr?.quarterNumber
    )
    .sort((a, b) => {
      const quarterA = getQuarterNumber(a) ?? 0;
      const quarterB = getQuarterNumber(b) ?? 0;
      return quarterB - quarterA;
    });

  // Only compute quarterly income if no annual report exists for the same year
  const latestYearQuarterDocuments = latestYearAnnualReturn
    ? []
    : latestYearTaxReturns
        .map((tr) => {
          const hasIncomeValues =
            tr?.combinedIncome != null && tr?.grossIncome != null;

          const calculatedNet = hasIncomeValues
            ? Number(tr.grossIncome) - Number(tr.combinedIncome)
            : null;

          return {
            year: Number(tr?.taxReturnYear),
            quarter: getQuarterNumber(tr),
            annualizedNet:
              tr?.netIncome != null
                ? Number(tr.netIncome)
                : tr?.taxBaseIncome != null
                  ? Number(tr.taxBaseIncome)
                  : calculatedNet,
          };
        })
        .filter(
          (tr) =>
            Number.isFinite(tr.year) &&
            Number.isFinite(tr.quarter) &&
            tr.quarter >= 1 &&
            tr.quarter <= 4 &&
            tr.annualizedNet != null &&
            Number.isFinite(tr.annualizedNet)
        );

  const selfEmployedIncomeResult = calculateModelo130Income(
    latestYearQuarterDocuments
  );

  const latestAnnualWithIncome = taxReturns
    .filter((tr) => tr?.reportType === "annual")
    .sort((a, b) => Number(b?.taxReturnYear) - Number(a?.taxReturnYear))
    .find((tr) => hasValidFallbackIncome(tr));

  const latestTaxReturnWithIncome =
    latestYearTaxReturns.find((tr) => hasValidFallbackIncome(tr)) || null;

  // Annual report takes priority over quarterly for the same year
  const fallbackTaxReturnForDisplay =
    latestAnnualWithIncome || latestTaxReturnWithIncome;

  const fallbackItrAnnualIncome = getFallbackAnnualIncome(
    fallbackTaxReturnForDisplay
  );

  const itrMonthlyIncome = selfEmployedIncomeResult?.success
    ? selfEmployedIncomeResult?.monthlyIncome
    : fallbackItrAnnualIncome
      ? Number(fallbackItrAnnualIncome) / 12
      : null;

  return (
    <>
      <div
        className={`main_doc_div mt-3 ${
          isFromReport ? "main_doc_div_report main_doc_div_report_full" : ""
        }`}
      >
        <div className="d-flex align-items-center gap-2 BorderB2">
          <img src={Twotone} alt="Vector Icon" className="vactorImg2" />
          <p className="docheader">{_t("incomeInformation")}</p>
        </div>
        <div className="row">
          {!llShowOnly && (
            <div className="col-md-4 col-6" style={{ cursor: "pointer" }}>
              <div className="Indo_form BorderB2 ">
                <p className="formlabel d-block">{_t("declaredIncome")}</p>
                <p className="placeholderTest">
                  €{convertCurrencyFormat(declaredIncome ?? 0)}
                </p>
                <p className="placeholderTest converted_text">
                  {user?.currency !== "EUR" && `(${_t("converted")})`}
                </p>
              </div>
            </div>
          )}
          {llShowOnly && (
            <div className="col-md-4 col-6">
              <div className="Indo_form BorderB2 ">
                <div className="d-flex align-items-center gap-1">
                  <p className="formlabel d-block">{_t("netIncomeFromBank")}</p>
                  <div className=" d-flex align-items-center gap-1">
                    {haveValue(
                      user?.bank_details?.userDataByProvider?.[0]?.providerName
                    ) &&
                      user?.bank_details?.userDataByProvider?.[0]
                        ?.providerName !== "Uploaded from CSV" &&
                      haveValue(recordedSalary?.salary) &&
                      recordedSalary?.salary > 0 && (
                        <img
                          src={CheckIcon0}
                          alt="Vector Icon"
                          className="validationIcon"
                        />
                      )}
                  </div>
                </div>
                <p className="placeholderTest">
                  {haveValue(recordedSalary?.salary) ? (
                    <div>
                      €
                      {!Number.isNaN(recordedSalary?.salary)
                        ? convertCurrencyFormat(recordedSalary?.salary)
                        : 0}{" "}
                      {haveValue(recordedSalary?.salary) &&
                        recordedSalary?.salary > 0 &&
                        `(${moment(
                          haveValue(user?.bankVerificationDate)
                            ? user?.bankVerificationDate
                            : user?.created_at
                        ).format("MMM YYYY")})`}
                    </div>
                  ) : (
                    <div>€0</div>
                  )}
                </p>
              </div>
            </div>
          )}
          {llShowOnly && haveValue(itrMonthlyIncome) && (
            <div className="col-md-4 col-6">
              <div
                className={`Indo_form BorderB2 ${
                  llShowOnly ? "cursor-pointer custom-tooltip" : ""
                }`}
                data-tooltip={_t("clickHereToSeeDetails")}
                onClick={() =>
                  llShowOnly &&
                  handleInfoModal(
                    forensicAnalysis?.["Tax Returns"] ??
                      forensicAnalysis?.["Tax returns"]
                  )
                }
              >
                <div className="d-flex align-items-center gap-1 ">
                  <p className="formlabel d-block ">
                    {_t("incomeFromTaxReport")}
                  </p>

                  {(
                    forensicAnalysis?.["Tax returns"] ??
                    forensicAnalysis?.["Tax Returns"]
                  )?.verifiedSource && (
                    <img
                      src={CheckIcon0}
                      alt="Verified"
                      className="validationIcon"
                    />
                  )}
                </div>
                <p className="placeholderTest">
                  €
                  {convertCurrencyFormat(
                    getUpdatedExchangeIncome(
                      originalUser?.current_workspace?.dea_rules_activated &&
                        deaWorkspace &&
                        (forensicAnalysis?.["Employment Type"]?.dataInJSON
                          ?.employmentType === "Employee" ||
                          forensicAnalysis?.["Employment Type"]?.dataInJSON
                            ?.employmentType ===
                            "Self-employed (Company Owner)")
                        ? selfEmployedIncomeResult?.success
                          ? selfEmployedIncomeResult?.monthlyIncome
                          : (fallbackTaxReturnForDisplay?.netIncomeAfterDeductions ||
                              0) / 12
                        : itrMonthlyIncome,
                      fallbackTaxReturnForDisplay?.currencyCode,
                      originalUser
                    )
                  )}{" "}
                  {selfEmployedIncomeResult?.success ? (
                    <>
                      ({latestYear}
                      {selfEmployedIncomeResult?.formsUsed?.[0]?.quarter
                        ? ` Q${selfEmployedIncomeResult.formsUsed[0].quarter}`
                        : ""}
                      )
                    </>
                  ) : fallbackTaxReturnForDisplay?.taxReturnYear ? (
                    <>({fallbackTaxReturnForDisplay?.taxReturnYear})</>
                  ) : null}
                </p>

                {fallbackTaxReturnForDisplay?.currencyCode !== "EUR" && (
                  <p className="placeholderTest converted_text">
                    ({_t("converted")})
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        {llShowOnly &&
          haveValue(forensicAnalysis?.Income?.dataInJSON?.payslips) &&
          forensicAnalysis?.Income?.dataInJSON?.payslips?.length > 0 &&
          (() => {
            const filtered = forensicAnalysis.Income.dataInJSON.payslips.filter(
              (p) => p?.date && typeof p?.netIncome === "number"
            );

            const byEmployer = {};
            for (const p of filtered) {
              const key = p?.employerName ?? "";
              if (!byEmployer[key]) byEmployer[key] = [];
              byEmployer[key].push(p);
            }

            const employerGroups = Object.entries(byEmployer)
              .map(([employer, payslips]) => {
                const sorted = [...payslips].sort(
                  (a, b) => new Date(b.date) - new Date(a.date)
                );
                return {
                  employer,
                  payslips: sorted.slice(0, 3),
                  latestDate: new Date(sorted[0].date),
                };
              })
              .sort((a, b) => b.latestDate - a.latestDate);

            return employerGroups.map(({ employer, payslips }) => (
              <div key={employer}>
                <div className="pb-0 BorderB2">
                  <p
                    style={{ lineHeight: "normal" }}
                    className="formlabel d-block"
                  >
                    {_t("payslip")} - ({employer})
                  </p>
                </div>
                <div className="row">
                  {payslips.map((data, index) => (
                    <div
                      key={`${data?.date}-${index}`}
                      className={`col-md-4 col-6 ${
                        haveValue(data) && llShowOnly
                          ? "cursor-pointer custom-tooltip"
                          : ""
                      }`}
                      data-tooltip={_t("clickHereToSeeDetails")}
                      onClick={() => {
                        if (haveValue(data) && llShowOnly) {
                          handleInfoModal(data);
                        }
                      }}
                    >
                      <div className="Indo_form BorderB2 payslip_wrapper">
                        <div className="d-flex align-items-center gap-1">
                          <p className="placeholderTest my-0">
                            €
                            {convertCurrencyFormat(
                              getUpdatedExchangeIncome(
                                data?.netIncome,
                                data?.currencyCode,
                                originalUser
                              ) ?? 0
                            )}{" "}
                            ({formatToStandardDate(data?.date)})
                          </p>
                          {((forensicAnalysis?.Income?.verifiedSource ===
                            true &&
                            data?.verified) ||
                            data?.payslipSourceVerified ||
                            (data?.verified &&
                              data?.socialSecuritySourceVerified)) && (
                            <div className="d-flex align-items-center gap-1">
                              <img
                                src={CheckIcon0}
                                alt="Vector Icon"
                                className="validationIcon"
                              />
                            </div>
                          )}
                        </div>
                        <p className="placeholderTest converted_text">
                          {data?.currencyCode !== "EUR" &&
                            `(${_t("converted")})`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        {llShowOnly &&
          (() => {
            const hasBankIncome =
              haveValue(recordedSalary?.salary) && recordedSalary?.salary > 0;
            const hasItrIncome = haveValue(itrMonthlyIncome);
            const hasPayslips =
              haveValue(forensicAnalysis?.Income?.dataInJSON?.payslips) &&
              forensicAnalysis?.Income?.dataInJSON?.payslips?.length > 0;
            const hasVerifiedSource =
              hasBankIncome || hasItrIncome || hasPayslips;

            if (hasVerifiedSource) return null;

            return (
              <div className="no-verified-income-placeholder">
                <span className="no-verified-income-placeholder-dot" />
                <p>
                  {_t(
                    "noVerifiedIncomeSourcesFound",
                    "Placeholder shown in monthly income detail view for landlords when no verified income sources exist"
                  )}
                </p>
              </div>
            );
          })()}
      </div>
    </>
  );
};

export default DetailsIncomeInfo;
